// Dependencies
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';

// Types
import { WorkVehicleType, TableProps, getWorkVehiclesColumnNames } from './types';
import {
  VehicleOwnership,
  getVehicleOwnershipLabel,
} from '../../../../../types/vehicles';

// Mocks
import { mockData } from './mocks';

// Styles
import { Container, Table, Tbody, Thead, Tr } from '../SummaryTablesStyles';

// Styled Components
import { TableContainerStyled, TdStyled, ThStyled } from './styles';
import Badge from '../../../Badge';

// ✅ NUEVO: Tabla para mostrar vehículos de obra (CONSTRUCTION) en resumen de actuación
export const WorkVehiclesTable = ({ data, ...props }: TableProps): JSX.Element => {
  const { t } = useTranslation(['tables', 'common']);
  const dataAll = data ? data : mockData;

  const columnHelper = createColumnHelper<WorkVehicleType>();
  const translatedColumnNames = useMemo(() => getWorkVehiclesColumnNames(t), [t]);

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.plate, {
        id: translatedColumnNames.plate.id,
        header: () => translatedColumnNames.plate.label,
      }),
      columnHelper.accessor((row) => row.model, {
        id: translatedColumnNames.model.id,
        header: () => translatedColumnNames.model.label,
      }),
      columnHelper.accessor((row) => row.category, {
        id: translatedColumnNames.category.id,
        header: () => translatedColumnNames.category.label,
      }),
      columnHelper.accessor((row) => row.driver, {
        id: translatedColumnNames.driver.id,
        header: () => translatedColumnNames.driver.label,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor((row) => row.ownership, {
        id: translatedColumnNames.ownership.id,
        header: () => translatedColumnNames.ownership.label,
        cell: (info) => {
          const ownership = info.getValue() as string;
          const variant =
            ownership === VehicleOwnership.INTERNAL
              ? 'utility-internal'
              : 'utility-external';
          const label = getVehicleOwnershipLabel(ownership as VehicleOwnership);
          return (
            <Badge variant={variant} size="small">
              {label}
            </Badge>
          );
        },
      }),
    ],
    [translatedColumnNames]
  );

  const table = useReactTable({
    data: dataAll,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Container>
      <TableContainerStyled>
        <Table {...props}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <ThStyled
                    key={header.id}
                    cell={header.id as 'plate' | 'model' | 'category' | 'ownership' | 'driver'}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </ThStyled>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TdStyled key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TdStyled>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainerStyled>
    </Container>
  );
};

